import { useCallback, useEffect, useMemo, useState } from "react";
import { BadgeDollarSign, Bell, Megaphone, Save, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { siteConfig } from "../../lib/siteConfig";

const defaultSettings = {
  notice_message: "",
  bad_beat_jackpot: 0,
};

const defaultNotification = {
  title: "Tonight's reload bonus",
  type: "announcement",
  message: "20% bonus is live until midnight.",
  target: "all",
};

const inputClass = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-[#2F6FA5]";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function targetLabel(value) {
  if (value === "verified") return "Verified";
  if (value === "unverified") return "Unverified";
  return "All";
}

function typeLabel(value) {
  if (value === "bonus") return "Bonus";
  if (value === "maintenance") return "Maintenance";
  return "Announcement";
}

export default function AdminCommunications() {
  const [settings, setSettings] = useState(defaultSettings);
  const [notification, setNotification] = useState(defaultNotification);
  const [history, setHistory] = useState([]);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/communications");
    setSettings({ ...defaultSettings, ...(data.settings || {}) });
    setHistory(data.history || []);
    setEligibleCount(Number(data.eligible_count || 0));
  }, []);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const noticeLength = useMemo(() => String(settings.notice_message || "").length, [settings.notice_message]);
  const messageLength = useMemo(() => String(notification.message || "").length, [notification.message]);

  const updateSettings = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const updateNotification = (key, value) => {
    setNotification((current) => ({ ...current, [key]: value }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        notice_message: String(settings.notice_message || "").slice(0, 300),
        bad_beat_jackpot: Number(settings.bad_beat_jackpot || 0),
      };
      const { data } = await api.put("/admin/communications", payload);
      setSettings({ ...defaultSettings, ...(data.settings || {}) });
      setHistory(data.history || []);
      setEligibleCount(Number(data.eligible_count || 0));
      toast.success("Communications saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const sendNotification = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const payload = {
        title: notification.title.trim(),
        type: notification.type,
        message: notification.message.trim(),
        target: notification.target,
      };
      const { data } = await api.post("/admin/communications/notifications", payload);
      setHistory(data.history || []);
      setEligibleCount(Number(data.eligible_count || 0));
      toast.success("Notification sent");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl p-3 sm:p-6 md:p-8">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <Megaphone className="h-6 w-6 text-[#2F6FA5]" />
          <h1 className="text-2xl font-semibold tracking-tight">Communications</h1>
        </div>
        <p className="mt-1 text-sm text-slate-400">What your players see on the club home page.</p>
      </header>

      <form onSubmit={saveSettings} className="mb-6 rounded-xl border border-[#2F6FA5]/25 bg-[#121214] p-4 sm:p-6">
        <div className="mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5 text-[#2F6FA5]" />
          <h2 className="text-lg font-semibold">Notice / welcome message</h2>
        </div>

        <label className="block">
          <textarea
            value={settings.notice_message}
            maxLength={300}
            onChange={(event) => updateSettings("notice_message", event.target.value)}
            className={`${inputClass} min-h-24 resize-y`}
            placeholder={`For Claim Reward\n\nPLEASE USE THIS LINK TO CLAIM REWARD www.${siteConfig.domain}`}
          />
          <span className="mt-1 block text-right text-xs text-slate-500">{noticeLength}/300</span>
        </label>

        <label className="mt-4 block">
          <span className="mb-2 block text-sm font-semibold">Bad-beat jackpot ($)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={settings.bad_beat_jackpot}
            onChange={(event) => updateSettings("bad_beat_jackpot", event.target.value)}
            className={inputClass}
          />
          <span className="mt-2 block text-xs text-slate-500">Shown as a top banner on the player club view.</span>
        </label>

        <button
          type="submit"
          disabled={savingSettings}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-[#2F6FA5] px-6 py-3 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {savingSettings ? "Saving..." : "Save"}
        </button>
      </form>

      <section className="rounded-xl border border-[#2F6FA5]/25 bg-[#121214] p-4 sm:p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#2F6FA5]" />
              <h2 className="text-lg font-semibold">In-app notifications</h2>
            </div>
            <p className="mt-1 text-sm text-slate-400">Send announcements to this club's members.</p>
          </div>
          <span className="rounded-full border border-[#2F6FA5]/40 px-3 py-1 text-xs font-bold text-[#2F6FA5]">
            {eligibleCount} eligible
          </span>
        </div>

        <form onSubmit={sendNotification} className="grid gap-4 lg:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold">Title</span>
            <input
              value={notification.title}
              maxLength={120}
              onChange={(event) => updateNotification("title", event.target.value)}
              className={inputClass}
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold">Type</span>
            <select
              value={notification.type}
              onChange={(event) => updateNotification("type", event.target.value)}
              className={inputClass}
            >
              <option value="announcement">Announcement</option>
              <option value="bonus">Bonus</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold">Message</span>
            <textarea
              value={notification.message}
              maxLength={1000}
              onChange={(event) => updateNotification("message", event.target.value)}
              className={`${inputClass} min-h-28 resize-y`}
              required
            />
            <span className="mt-1 block text-right text-xs text-slate-500">{messageLength}/1000</span>
          </label>

          <div className="flex flex-col gap-4">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">Target</span>
              <select
                value={notification.target}
                onChange={(event) => updateNotification("target", event.target.value)}
                className={inputClass}
              >
                <option value="all">All eligible members</option>
                <option value="verified">Verified members</option>
                <option value="unverified">Unverified members</option>
              </select>
            </label>

            <button
              type="submit"
              disabled={sending}
              className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A100] px-6 py-3 text-sm font-bold text-black hover:bg-[#FFC533] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </form>

        <div className="mt-8 overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-[760px] w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3 text-right">Sent</th>
                <th className="px-4 py-3 text-right">Date</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    {loading ? "Loading..." : "No notifications sent yet."}
                  </td>
                </tr>
              )}
              {history.map((row) => (
                <tr key={row.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{row.title}</div>
                    <div className="mt-1 max-w-md truncate text-xs text-slate-400">{row.message}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs">{typeLabel(row.type)}</span>
                  </td>
                  <td className="px-4 py-3">{targetLabel(row.target)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{Number(row.sent_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-slate-400">{formatDate(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
